import type { JSX } from 'react';

import * as React from 'react';
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

const findFirstFocusableDescendant = (
    startElement: HTMLElement,
): HTMLElement | null => {
    const focusableSelector =
        'button, a[href], input, select, textarea, details, summary [tabindex], [contenteditable]';

    const focusableDescendants = startElement.querySelector(
        focusableSelector,
    ) as HTMLElement;

    return focusableDescendants;
};
const focusNearestDescendant = (
    startElement: HTMLElement,
): HTMLElement | null => {
    const el = findFirstFocusableDescendant(startElement);
    el?.focus();
    return el;
};

const isKeyboardInput = (
    event: MouseEvent | PointerEvent | React.MouseEvent<HTMLElement>,
): boolean => {
    if ('pointerId' in event && 'pointerType' in event) {
        return event.pointerId === -1 && event.pointerType === '';
    }

    return event?.detail === 0;
};

type DropDownContextType = {
    registerItem: (ref: React.RefObject<null | HTMLButtonElement>) => void;
};

const DropDownContext = React.createContext<DropDownContextType | null>(null);

const dropDownPadding = 4;

export function DropDownItem({
    children,
    className,
    onClick,
    title,
}: {
    children: React.ReactNode;
    className: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    title?: string;
}) {
    const ref = useRef<null | HTMLButtonElement>(null);

    const dropDownContext = React.useContext(DropDownContext);

    if (dropDownContext === null) {
        throw new Error('DropDownItem must be used within a DropDown');
    }

    const { registerItem } = dropDownContext;

    useEffect(() => {
        if (ref && ref.current) {
            registerItem(ref);
        }
    }, [ref, registerItem]);

    return (
        <button
            className={className}
            onClick={onClick}
            ref={ref}
            title={title}
            type="button">
            {children}
        </button>
    );
}

function DropDownItems({
    children,
    dropDownRef,
    onClose,
    autofocus,
}: {
    children: React.ReactNode;
    dropDownRef: React.RefObject<HTMLDivElement>;
    onClose: () => void;
    autofocus: boolean;
}) {
    const [items, setItems] =
        useState<React.RefObject<null | HTMLButtonElement>[]>();
    const [highlightedItem, setHighlightedItem] =
        useState<React.RefObject<null | HTMLButtonElement>>();

    const registerItem = useCallback(
        (itemRef: React.RefObject<null | HTMLButtonElement>) => {
            setItems((prev) => (prev ? [...prev, itemRef] : [itemRef]));
        },
        [setItems],
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const key = event.key;
        if (key === 'Escape') {
            onClose();
        }
        if (!items) {
            return;
        }

        if (['Escape', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
            event.preventDefault();
        }

        if (key === 'Escape' || key === 'Tab') {
            onClose();
        } else if (key === 'ArrowUp') {
            setHighlightedItem((prev) => {
                if (!prev) {
                    return items[0];
                }
                const index = items.indexOf(prev) - 1;
                return items[index === -1 ? items.length - 1 : index];
            });
        } else if (key === 'ArrowDown') {
            setHighlightedItem((prev) => {
                if (!prev) {
                    return items[0];
                }
                return items[items.indexOf(prev) + 1];
            });
        }
    };

    const contextValue = useMemo(
        () => ({
            registerItem,
        }),
        [registerItem],
    );

    useEffect(() => {
        if (items && !highlightedItem) {
            setHighlightedItem(items[0]);
        }

        if (highlightedItem && highlightedItem.current) {
            highlightedItem.current.focus();
        }
    }, [items, highlightedItem]);

    useEffect(() => {
        if (autofocus && dropDownRef.current) {
            focusNearestDescendant(dropDownRef.current);
        }
    }, [autofocus, dropDownRef]);

    return (
        <DropDownContext.Provider value={contextValue}>
            <div className="essay-editor-dropdown" ref={dropDownRef} onKeyDown={handleKeyDown}>
                {children}
            </div>
        </DropDownContext.Provider>
    );
}

export default function DropDown({
    disabled = false,
    buttonLabel,
    buttonAriaLabel,
    buttonTitle,
    buttonClassName,
    buttonIcon,
    children,
    stopCloseOnClickSelf,
    isOpen,
    handleToggle,
}: {
    disabled?: boolean;
    buttonAriaLabel?: string;
    buttonTitle?: string;
    buttonClassName: string;
    buttonIcon?: ReactNode;
    buttonLabel?: string;
    children: ReactNode;
    stopCloseOnClickSelf?: boolean;
    isOpen?: boolean;
    handleToggle?: (open: boolean) => void;
}): JSX.Element {
    const dropDownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [showDropDown, setShowDropDown] = useState(false);
    const [shouldAutofocus, setShouldAutofocus] = useState(false);
    const controlled = isOpen !== undefined;
    const currentIsOpen = controlled ? isOpen : showDropDown;

    const handleClose = () => {
        if (controlled && handleToggle) {
            handleToggle(!currentIsOpen)
        } else {
            setShowDropDown(false);
        }
        if (buttonRef && buttonRef.current) {
            buttonRef.current.focus();
        }
    };

    useEffect(() => {
        const button = buttonRef.current;
        const dropDown = dropDownRef.current;

        if (currentIsOpen && button !== null && dropDown !== null) {
            const { top, left } = button.getBoundingClientRect();
            dropDown.style.top = `${top + button.offsetHeight + dropDownPadding}px`;
            dropDown.style.left = `${Math.min(
                left,
                window.innerWidth - dropDown.offsetWidth - 20,
            )}px`;
        }
    }, [dropDownRef, buttonRef, currentIsOpen]);

    useEffect(() => {
        const button = buttonRef.current;

        if (button !== null && currentIsOpen) {
            const handle = (event: PointerEvent) => {
                const target = event.target;
                if (!(target instanceof Node)) {
                    return;
                }

                const targetIsDropDownItem =
                    dropDownRef.current && dropDownRef.current.contains(target);
                if (stopCloseOnClickSelf && targetIsDropDownItem) {
                    return;
                }

                if (!button.contains(target)) {
                    if (controlled && handleToggle) {
                        handleToggle(false)
                    } else {
                        setShowDropDown(false);
                    }


                    if (targetIsDropDownItem && isKeyboardInput(event)) {
                        button.focus();
                    }
                }
            };
            document.addEventListener('click', handle);

            return () => {
                document.removeEventListener('click', handle);
            };
        }
    }, [dropDownRef, buttonRef, currentIsOpen, stopCloseOnClickSelf]);

    useEffect(() => {
        const handleButtonPositionUpdate = () => {
            if (currentIsOpen) {
                const button = buttonRef.current;
                const dropDown = dropDownRef.current;
                if (button !== null && dropDown !== null) {
                    const { top } = button.getBoundingClientRect();
                    const newPosition = top + button.offsetHeight + dropDownPadding;
                    if (newPosition !== dropDown.getBoundingClientRect().top) {
                        dropDown.style.top = `${newPosition}px`;
                    }
                }
            }
        };

        document.addEventListener('scroll', handleButtonPositionUpdate);

        return () => {
            document.removeEventListener('scroll', handleButtonPositionUpdate);
        };
    }, [buttonRef, dropDownRef, currentIsOpen]);

    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (controlled && handleToggle) {
            handleToggle(!currentIsOpen)
        } else {
            setShowDropDown(!showDropDown);
        }
        setShouldAutofocus(isKeyboardInput(e));
    };

    return (
        <>
            <button
                type="button"
                disabled={disabled}
                aria-label={buttonAriaLabel || buttonLabel}
                className={buttonClassName}
                title={buttonTitle}
                onClick={handleOnClick}
                ref={buttonRef}>
                {buttonIcon}
                {buttonLabel && (
                    <span className="text dropdown-button-text">{buttonLabel}</span>
                )}
                <i className="chevron-down" />
            </button>

            {currentIsOpen &&
                createPortal(
                    <DropDownItems
                        dropDownRef={dropDownRef}
                        onClose={handleClose}
                        autofocus={shouldAutofocus} children={children} />,
                    document.body,
                )}
        </>
    );
}
